import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAppStore } from "./app";
import type { BackOrigin } from "../../features/shell/back";

export interface OpenContainerOptions {
  /** Record where the container was opened from, so the detail's back link is deterministic. */
  from?: BackOrigin;
  /** The search query string to restore when opening a result (`from: "search"`). */
  search?: string;
}

/**
 * The single "open this container" entry point for world-scoped surfaces
 * (catalog-entry-sharing 11): the store action scopes the container's catalog
 * entry and mirrors it, then this hook routes to its merged container page. A
 * container the caller can no longer read falls back to /browse; a transient
 * lookup failure still routes to the container page, which owns its
 * loading/retry state.
 *
 * @returns the opener; pass the back-contract origin per call, resolves true
 *   when the container was opened.
 */
export function useOpenContainer(): (id: string, options?: OpenContainerOptions) => Promise<boolean> {
  const navigate = useNavigate();
  const openContainer = useAppStore((s) => s.openContainer);
  return useCallback(
    async (id: string, options: OpenContainerOptions = {}) => {
      const result = await openContainer(id);
      if (result === "missing") {
        navigate("/browse");
        return false;
      }
      // No origin keeps the plain push: ordinary navigation has no back state.
      const { from, search } = options;
      if (!from) {
        navigate(`/browse/${id}`);
      } else {
        const state = { from, ...(from === "search" ? { search: search ?? "" } : {}) };
        navigate(`/browse/${id}`, { state });
      }
      return result === "opened";
    },
    [navigate, openContainer],
  );
}
